import dayjs from 'dayjs';

// Constants
import {
  PROJECT_STATUS_VARIANTS,
  TIMELINE_EVENT_ICON_MAP,
  PROJECT_STATUS_LABEL,
  ADDITIONAL_ROLE_COLOR_MAP,
  PROJECT_ACTIVITY_TYPE_VARIANTS,
  PROJECT_ACTIVITY_TYPE_LABEL,
  ROLE_BADGE_COLOR_DEFAULT,
  EMPLOYEE_PROJECT_ACTION_TITLE_MAP,
} from '@/constants/project';
import {
  MEMBER_ACTION_LABEL_MAP,
  MEMBER_ACTION_SUCCESS_MESSAGE_MAP,
} from '@/constants/member';
import { HREF_TO_LABEL_TOOLTIP } from '@/constants/tooltip';
import {
  EMPLOYEE_ACTIVITY_GROUPS,
  EmployeeActivityGroup,
} from '@/constants/employee';
import {
  ACTIVITY_LABELS,
  MEMBER_ACTION_LABELS,
  EXCLUDED_DESCRIPTION_TYPES,
  EXCLUDED_CALLBACK_TYPES,
} from '@/constants/activity';
import {
  DEFAULT_EMAIL_DOMAIN,
  FALLBACK_SRC,
  ROLE_ITEMS,
  DATE_FORMATS,
} from '@shared/constants';
import {
  CONTENT_TOOLTIP,
  EmployeeActivity,
  EmployeeProjectInfoResponse,
  MemberAddPayloadSocket,
  MemberProjectActivitySocket,
  PROJECT_DATE_STATUS,
  ProjectInfoResponse,
  UserProfile,
} from '@/types';

// Types
import {
  ActionItemType,
  ActivityType,
  POSITION,
  ProjectActivity,
  ProjectActivityData,
  ProjectBase,
  ProjectInfo,
  ProjectMember,
  ProjectMemberActivity,
  ProjectMemberAdditionalRole,
  ProjectStatus,
} from '@shared/types';
import { ArgsType } from '@/types/chat';
import {
  EmployeeProjectAction,
  EmployeeProjectInfoCardData,
  PROJECT_INFO_FIELD,
  ProjectEventName,
} from '@/types/project';

// Utils
import {
  formatISODateString,
  formatTimeString,
  toPlural,
  formatDateTimeDisplay,
  formatDateStringToTimezone,
} from '@shared/utils';

// Types
import { ProjectMemberViewMode } from '@/stores/userConfig/types';

export type variant =
  | 'default'
  | 'complete'
  | 'waiting'
  | 'active'
  | 'pending'
  | 'reopen'
  | 'outline'
  | 'secondary'
  | 'destructive'
  | 'archive'
  | null
  | undefined;

/**
 * Get valid additional role from tag
 * @param tag - The tag value to validate
 * @returns ProjectMemberAdditionalRole if valid, null otherwise
 */
export const getValidAdditionalRole = (
  tag?: string | null,
): ProjectMemberAdditionalRole | null =>
  Object.values(ProjectMemberAdditionalRole).find(
    (value) => value === tag?.trim()?.toLowerCase(),
  ) ?? null;

/**
 * Get color based on role
 */
export const getColorBaseOnRole = (role: string) =>
  ADDITIONAL_ROLE_COLOR_MAP[role as ProjectMemberAdditionalRole] ||
  ROLE_ITEMS.find((item) => item.name === role)?.data.color ||
  ROLE_BADGE_COLOR_DEFAULT;

/**
 * Get member color based on tag or role
 */
const getMemberColor = (
  role: string,
  additionalRole?: ProjectMemberAdditionalRole | null,
) =>
  additionalRole
    ? ADDITIONAL_ROLE_COLOR_MAP[additionalRole]
    : getColorBaseOnRole(role);

/**
 * Normalize value to array
 * @param value - Single item, array, or undefined
 * @returns Array of items or empty array
 */
export const normalizeToArray = <T>(value?: T | T[]): T[] => {
  return Array.isArray(value) ? value : value ? [value] : [];
};

/**
 * Entries and find the path for a value in PROJECT_STATUS_VARIANTS
 * @param string - The status project
 * @returns string - The variant with value map text in PROJECT_STATUS_VARIANTS
 */
export const getVariantFromStatus = (status: string) => {
  const matchedVariantStatus = Object.entries(PROJECT_STATUS_VARIANTS).find(
    ([variant]) => status.includes(variant),
  );

  return matchedVariantStatus?.[1] as variant;
};

/**
 * Get project status display (variant and label) based on status and archived flag
 * @param status - The project status
 * @param archived - Whether the project is archived
 * @returns Object with variant and label for badge display
 */
export const getProjectStatusDisplay = (
  status: ProjectStatus | string | null | undefined,
  archived = false,
): { variant: variant; label: string } => {
  if (archived)
    return {
      variant: PROJECT_ACTIVITY_TYPE_VARIANTS[ActivityType.PROJECT_ARCHIVE],
      label: PROJECT_ACTIVITY_TYPE_LABEL[ActivityType.PROJECT_ARCHIVE],
    };

  if (!status)
    return {
      variant: PROJECT_STATUS_VARIANTS[ProjectStatus.ACTIVE],
      label: PROJECT_STATUS_LABEL[ProjectStatus.ACTIVE],
    };

  return {
    variant:
      getVariantFromStatus(status) ??
      PROJECT_STATUS_VARIANTS[ProjectStatus.ACTIVE],
    label: getLabelFromStatus(status) ?? status,
  };
};

/**
 * Builds a readable event title based on activity type, member count,
 * and label mode.
 *
 * - Project member activities: can include member count (e.g. "3 members have joined").
 * - Employee or short-label mode: use predefined simple labels.
 *
 * @param activityType - The activity type (e.g. PROJECT_MEMBER_JOIN).
 * @param memberCount - Optional member count for project member activities.
 * @param isShortLabel - If true, use simple labels instead of count logic.
 * @returns Event title string.
 */
export const generateEventTitle = (
  activityType: ActivityType,
  memberCount?: number,
  isShortLabel = false,
): string => {
  const baseConfig =
    TIMELINE_EVENT_ICON_MAP[
      activityType as keyof typeof TIMELINE_EVENT_ICON_MAP
    ];

  if (baseConfig) {
    // Handle Project Member activities with count logic
    const action =
      MEMBER_ACTION_LABELS[activityType as keyof typeof MEMBER_ACTION_LABELS];

    if (action) {
      // For Employee context, use simple labels from ACTIVITY_LABELS
      if (isShortLabel) {
        return ACTIVITY_LABELS[activityType as keyof typeof ACTIVITY_LABELS];
      }

      // For Project context, use member count logic with toPlural util
      if (!memberCount || memberCount <= 0) {
        return `Member ${action}`;
      }

      const memberWord = toPlural('member', memberCount);
      const verb = memberCount === 1 ? 'has' : 'have';
      return memberCount === 1
        ? `A ${memberWord} ${verb} ${action}`
        : `${memberCount} ${memberWord} ${verb} ${action}`;
    }

    // For Employee activities and other project activities, generate label from activityType
    return ACTIVITY_LABELS[activityType as keyof typeof ACTIVITY_LABELS];
  }

  return 'Activity event';
};

/**
 * Converts schedule type to action label
 * @param scheduleType - Activity type with 'project_' prefix
 * @returns Action label or 'update' as fallback
 */
const getScheduledActionLabel = (scheduleType: string): string =>
  scheduleType.replace('project_', '').replaceAll('_', ' ') || 'update';

const isProjectBaseData = (data: ProjectActivityData): data is ProjectBase =>
  !!(data && 'scheduleType' in data && 'scheduleTime' in data);

/**
 * Generates label for scheduled project activities
 * @returns Formatted schedule message or fallback to ACTIVITY_LABELS if data incomplete
 */
export const getScheduledActivityLabel = (
  event: ProjectActivity | EmployeeActivity,
): string => {
  const data = event.data as ProjectActivity['data'];

  if (!isProjectBaseData(data)) {
    return ACTIVITY_LABELS[ActivityType.PROJECT_SCHEDULE_ACTIVITY];
  }

  if (!data.scheduleType || !data.scheduleTime) {
    return ACTIVITY_LABELS[ActivityType.PROJECT_SCHEDULE_ACTIVITY];
  }

  const action = getScheduledActionLabel(data.scheduleType);

  const scheduledDateTime = formatDateStringToTimezone(
    data.scheduleTime,
    DATE_FORMATS.AT_TIME_ON_DATE,
  );

  return `The project is scheduled to ${action}\n${scheduledDateTime}`;
};

export const formattedActivityDates = (value: ProjectMember) => {
  const { histories } = value;
  const activityDates: { date: Date; type: PROJECT_DATE_STATUS }[] = [];
  let hasSeenJoin = false;

  histories.forEach((history) => {
    const { type, time } = history;
    let processedType = type as PROJECT_DATE_STATUS;

    // If this is a JOIN type
    if (type === PROJECT_DATE_STATUS.JOINED) {
      if (hasSeenJoin) {
        // From second JOIN onwards, convert to RESUMED
        processedType = PROJECT_DATE_STATUS.RESUMED;
      } else {
        // First JOIN, keep as is and mark that we've seen a JOIN
        hasSeenJoin = true;
      }
    }

    activityDates.push({
      date: new Date(time),
      type: processedType,
    });
  });

  return activityDates;
};

export type label =
  | 'waiting for kick-off'
  | 'active'
  | 'completed'
  | 'paused'
  | 'resumed'
  | 'waiting'
  | 'complete'
  | 'pending'
  | 'reopen'
  | null
  | undefined;

/**
 * Entries and find the path for a value in PROJECT_STATUS_LABEL
 * @param string - The status project
 * @returns string - The label with value map text in PROJECT_STATUS_LABEL
 */
export const getLabelFromStatus = (status: string) => {
  const matchedLabelStatus = Object.entries(PROJECT_STATUS_LABEL).find(
    ([label]) => status.includes(label),
  );

  return matchedLabelStatus?.[1] as label;
};

/**
 * Get variant from project activity type
 * @param type - The project activity type (enum or string)
 * @returns variant - The variant for the activity type
 */
export const getVariantFromActivityType = (
  type?: ActivityType | string | null,
): variant => {
  if (!type) return 'active';

  const variantMap = PROJECT_ACTIVITY_TYPE_VARIANTS as Record<string, string>;
  return (variantMap[type] as variant) || 'active';
};

/**
 * Get label from project activity type
 * @param type - The project activity type (enum or string)
 * @returns string - The label for the activity type
 */
export const getLabelFromActivityType = (
  type?: ActivityType | string | null,
  status?: ProjectStatus,
): label => {
  if (!type) return (status || ProjectStatus.ACTIVE) as unknown as label;

  const labelMap = PROJECT_ACTIVITY_TYPE_LABEL as Record<string, string>;
  return (
    (labelMap[type] as label) || (ProjectStatus.ACTIVE as unknown as label)
  );
};

/**
 * Checks if an event should display a description based on its type
 * @param eventType - The project activity type
 * @returns Boolean indicating if description should be shown
 */
export const shouldShowTimelineDescription = (
  eventType: ActivityType | null | undefined,
): boolean => {
  if (!eventType) return false;

  return !EXCLUDED_DESCRIPTION_TYPES.includes(eventType);
};

/**
 * Entries and find the path for a value in HREF_TO_LABEL_TOOLTIP
 * @param string - The href navigation project
 * @returns string - The content tooltip hover icon navigation with value map text in HREF_TO_LABEL_TOOLTIP
 */
export const getContentTooltipFromHrefNav = (href: string) => {
  const matchedContentTooltip = Object.entries(HREF_TO_LABEL_TOOLTIP).find(
    ([contentTooltip]) => href.includes(contentTooltip),
  );

  return matchedContentTooltip?.[1] as CONTENT_TOOLTIP;
};

/**
 * Get event description from activity
 * Returns scheduleDesc for pending scheduled activities, or data.desc for others
 * @param event - The project activity or employee activity
 * @returns The event description or empty string
 */
export const getEventDescription = (
  event: ProjectActivity | EmployeeActivity,
): string => {
  // Scheduled activities: only show note while the schedule is still pending
  if (event.type === ActivityType.PROJECT_SCHEDULE_ACTIVITY) {
    const data = event.data as ProjectBase;
    const isPending =
      data?.scheduleTime && dayjs(data.scheduleTime).isAfter(dayjs());

    return isPending ? data.scheduleDesc || '' : '';
  }

  // Official activities: return desc directly
  return (event.data && 'desc' in event.data && event.data.desc) || '';
};

/**
 * Check if event has member data
 * @param event - The project activity
 * @returns boolean - Whether the event has member data
 */
export const hasMemberData = (event: ProjectActivity): boolean => {
  return !!(event.data && 'user' in event.data && event.data.user);
};

/**
 * Check if an activity belongs to a specific group
 * @param eventType - The activity type
 * @param options - Configuration options for group checking
 * @returns boolean - Whether the activity matches the group criteria
 */
export const isActivityInGroup = (
  eventType: ActivityType | null | undefined,
  options: {
    targetGroup: EmployeeActivityGroup;
    exclude?: boolean;
  },
): boolean => {
  if (!eventType) return false;

  const activityGroup = EMPLOYEE_ACTIVITY_GROUPS[eventType];

  if (activityGroup === undefined) return false;

  return options.exclude
    ? activityGroup !== options.targetGroup
    : activityGroup === options.targetGroup;
};

/**
 * Check if event is a user relationship activity (mentor/mentee/supporter assignments)
 * @param eventType - The activity type
 * @returns boolean - Whether the event is a user relationship activity
 */
export const isUserRelationshipActivity = (
  eventType: ActivityType | null | undefined,
): boolean =>
  isActivityInGroup(eventType, {
    targetGroup: EmployeeActivityGroup.USER,
  });

/**
 * Check if event is an Employee activity (any type except User relationship group)
 * @param eventType - The activity type
 * @returns boolean - Whether the event is any employee activity (excluding User relationship group)
 */
export const isEmployeeActivity = (
  eventType: ActivityType | null | undefined,
): boolean =>
  isActivityInGroup(eventType, {
    targetGroup: EmployeeActivityGroup.USER,
    exclude: true,
  });

/**
 * Calculate event positioning for timeline layout
 * Handles alternating left/right positioning and connector requirements
 */
export const getEventPosition = (
  eventIndexInGroup: number,
  isDesktop: boolean,
) => {
  const isLeftPosition = eventIndexInGroup % 2 === 0;

  const isLeft = isDesktop && isLeftPosition;
  const contentPosition = isLeft ? POSITION.LEFT : POSITION.RIGHT;

  return {
    isLeft,
    contentPosition,
  };
};

/**
 * Counts unique member names from an event group.
 *
 * - Returns the number of unique member names.
 * - Falls back to event group length if no names found.
 * - Returns undefined if no event group provided.
 */
export const countUniqueMemberNames = (
  eventGroup?: ProjectActivity[] | EmployeeActivity[],
): number | undefined => {
  if (!eventGroup?.length) return undefined;

  const uniqueNames = new Set<string>();

  for (const event of eventGroup) {
    // Skip events that don't have member data
    if (!hasMemberData(event as ProjectActivity)) continue;

    // Extract member name from activity data
    const memberName = (event.data as ProjectMemberActivity)?.name;

    if (memberName) uniqueNames.add(memberName);
  }

  return uniqueNames.size || eventGroup.length;
};

/**
 * Format event data for display
 * Handles title generation and time formatting
 */
export const formatEventData = (
  event: ProjectActivity | EmployeeActivity,
  eventGroup?: ProjectActivity[] | EmployeeActivity[],
  isShortLabel = false,
) => {
  const { type, occurredAt } = event;
  const memberCount = countUniqueMemberNames(eventGroup);

  const eventTitle =
    type === ActivityType.PROJECT_SCHEDULE_ACTIVITY
      ? getScheduledActivityLabel(event)
      : generateEventTitle(type, memberCount, isShortLabel);

  const eventTime = formatTimeString(occurredAt || '');

  return { eventTitle, eventTime };
};

/**
 * Check if a description content is short enough to be considered single paragraph
 * Based on actual text content length, not HTML structure
 * @param description - The description to check
 * @returns Boolean indicating if description content is <= 70 characters (should use text-right)
 */
export const isSingleParagraph = (description: string) => {
  // Remove all HTML tags and get only the text content
  const textContent = description.replace(/<[^>]*>/g, '').trim();

  // Check if the actual text content is 70 characters or less
  return textContent.length <= 70;
};

export const normalizedDataProjectMember = (
  members: (ProjectMember & { user?: string })[],
  projectId: string,
  users: UserProfile[],
) =>
  members.map((member) => {
    const matchUser = users.find((user) =>
      user.email?.includes(member.user || member.username || ''),
    );

    const validTag = getValidAdditionalRole(member.tag);

    return {
      ...member,
      id: matchUser?.id || member.id || '',
      avatar: member.avatar || matchUser?.avatar || FALLBACK_SRC.AVATAR,
      username: member.username || matchUser?.username || '',
      name: member.name || matchUser?.name || '',
      projectId,
      ...(validTag && { tag: validTag }),
      color: getMemberColor(member.role, validTag),
    };
  });

export const updatedDesc = (value: string) => {
  return value.replace(
    /class=["']([^"']*\btext-frost-900\b[^"']*)["']/g,
    (_match, classNames) => {
      const newClassNames = classNames
        .split(/\s+/)
        .filter((cls: string) => cls !== 'text-frost-900')
        .join(' ');
      return `class="${newClassNames}"`;
    },
  );
};

/**
 * Determine the appropriate view mode based on screen width
 * On devices < 1024px (tablet and mobile), use GRID mode
 * On desktop devices >= 1024px, use LIST mode as default
 */
export const getInitialViewMode = (): ProjectMemberViewMode => {
  // During SSR, default to LIST mode
  if (typeof window === 'undefined') {
    return ProjectMemberViewMode.LIST;
  }

  // For devices < 1024px (tablet and mobile), use GRID mode
  // For desktop devices >= 1024px, use LIST mode
  return window.innerWidth < 1024
    ? ProjectMemberViewMode.GRID
    : ProjectMemberViewMode.LIST;
};

export const shouldTriggerCallback = (item: ActionItemType) =>
  !EXCLUDED_CALLBACK_TYPES.has(item.type);

/**
 * Extract email from a project member
 *
 * This function determines the email address for a project member by checking multiple sources:
 * 1. If the `user` field contains an email (has '@'), use it directly
 * 2. If `username` exists, construct email by appending the default domain
 * 3. Fallback to the `user` field even if it doesn't contain '@'
 *
 * @param projectMember - The project member object containing user/username information
 * @returns The email address as a string, or empty string if no valid email can be determined
 */
export const getMemberEmail = (projectMember: ProjectMember): string => {
  // If user field contains email, use it
  if (projectMember.user && projectMember.user.includes('@')) {
    return projectMember.user;
  }

  // If username exists, construct email with default domain
  if (projectMember.username) {
    return `${projectMember.username}${DEFAULT_EMAIL_DOMAIN}`;
  }

  // Fallback to user field even if it doesn't contain @
  return projectMember.user || '';
};

/**
 * Filters out invalid project members (those without name or username)
 * Also filters out null/undefined members
 * @param members - Array of project members or null
 * @returns Array of valid project members with both name and username
 */
export const filterValidMembers = (
  members: ProjectMember[] | null,
): ProjectMember[] =>
  members?.filter(
    (member) => member?.name?.trim() && member?.username?.trim(),
  ) ?? [];

/**
 * Normalize members data by merging with activities data
 * Activities contain additional information like name and avatar that may be missing in members
 * @param members - Array of members from result.data.members
 * @param activities - Array of activities from result.data.activities
 * @returns Normalized array of members with complete data
 */
export const normalizeMembersWithActivities = (
  members: ProjectMember[],
  activities?: ProjectActivity[],
): ProjectMember[] => {
  if (!activities?.length) return members;

  return members.map((member) => {
    // Find matching activity by email
    const memberEmail = getMemberEmail(member);
    const matchingActivity = activities.find((activity) => {
      const activityData = activity.data as ProjectMemberActivity;
      return activityData?.user === memberEmail;
    });

    if (!matchingActivity) return member;

    const activityData = matchingActivity.data as ProjectMemberActivity;

    // Merge missing fields from activity data
    return {
      ...member,
      name: member.name || activityData?.name || '',
      avatar: member.avatar || activityData?.avatar || '',
      username: member.username || memberEmail.split('@')[0] || '',
    };
  });
};

export const toUsernameKey = (value?: string): string =>
  (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Resolve LLM-extracted usernames to exact stored values by alphanumeric key,
 * falling back to the original candidate when no member matches.
 */
export const resolveMemberUsernames = (
  candidates: string[] | undefined,
  members?: ProjectMember[],
): string[] => {
  if (!candidates?.length) return [];

  return Array.from(
    new Set(
      candidates
        .map((candidate) => {
          const trimmed = candidate.trim();
          const key = toUsernameKey(trimmed);

          return (
            members?.find((member) => toUsernameKey(member.username) === key)
              ?.username ?? trimmed
          );
        })
        .filter(Boolean),
    ),
  );
};

/**
 * Merge member update while preserving existing non-empty values
 */
const mergeMemberPreservingData = (
  existing: ProjectMember,
  update: ProjectMember,
): ProjectMember => ({
  ...existing,
  ...update,
  name: update.name || existing.name,
  id: update.id || existing.id,
  username: update.username || existing.username,
  avatar:
    update.avatar === FALLBACK_SRC.AVATAR &&
    existing.avatar !== FALLBACK_SRC.AVATAR
      ? existing.avatar
      : update.avatar || existing.avatar,
  tag: update.tag ?? undefined,
});

/**
 * Update member list by merging updates or adding new members (matched by email)
 */
export const updateMemberList = (
  memberUpdates: ProjectMember[],
  memberList: ProjectMember[],
): ProjectMember[] => {
  // Start with a copy of the original member list
  const updatedMembers = [...memberList];

  // Process each member in the update list
  memberUpdates.forEach((memberUpdate) => {
    const memberEmail = getMemberEmail(memberUpdate);
    if (!memberEmail) return;

    // Find the index of the member with matching email
    const existingIndex = updatedMembers.findIndex(
      (member) => getMemberEmail(member) === memberEmail,
    );

    if (existingIndex !== -1 && updatedMembers[existingIndex]) {
      updatedMembers[existingIndex] = mergeMemberPreservingData(
        updatedMembers[existingIndex],
        memberUpdate,
      );
    } else {
      updatedMembers.push({
        ...memberUpdate,
        username: memberEmail.split('@')[0],
        avatar: memberUpdate.avatar || FALLBACK_SRC.AVATAR,
      });
    }
  });

  return updatedMembers;
};

/**
 * Delete members from member list by matching email addresses
 * @param emailsToRemove - Array of email addresses to remove
 * @param memberList - Original member list to remove members from
 * @returns Updated member list with specified members removed
 */
export const deleteMembers = (
  emailsToRemove: string[],
  memberList: ProjectMember[],
): ProjectMember[] =>
  memberList.filter((existingMember) => {
    const existingMemberEmail = getMemberEmail(existingMember);

    return !emailsToRemove.includes(existingMemberEmail);
  });

export const sortProjectMembers = (members: ProjectMember[]) =>
  members.sort((a, b) => {
    // Get the most recent history for each member
    const getLatestHistory = (member: ProjectMember) => {
      if (!member.histories || member.histories.length === 0) return null;

      return member.histories.reduce((latest, current) => {
        const currentTime = dayjs(current.time);
        const latestTime = dayjs(latest.time);
        return currentTime.isAfter(latestTime) ? current : latest;
      });
    };

    const latestHistoryA = getLatestHistory(a);
    const latestHistoryB = getLatestHistory(b);

    // Check if members have LEFT or COMPLETED status in their latest history
    const isLeftA = latestHistoryA?.type === PROJECT_DATE_STATUS.LEFT;
    const isLeftB = latestHistoryB?.type === PROJECT_DATE_STATUS.LEFT;
    const isCompletedA = latestHistoryA?.type === PROJECT_DATE_STATUS.COMPLETED;
    const isCompletedB = latestHistoryB?.type === PROJECT_DATE_STATUS.COMPLETED;

    // Check if members have tag value
    const hasTagA = !!a.tag;
    const hasTagB = !!b.tag;

    // Priority order:
    // 1. Active members without tag (highest priority)
    // 2. Active members with tag
    // 3. COMPLETED members without tag
    // 4. COMPLETED members with tag
    // 5. LEFT members without tag
    // 6. LEFT members with tag (lowest priority)

    // Determine if member is active (not LEFT and not COMPLETED)
    const isActiveA = !isLeftA && !isCompletedA;
    const isActiveB = !isLeftB && !isCompletedB;

    // Active members always come first
    if (isActiveA && !isActiveB) return -1;
    if (!isActiveA && isActiveB) return 1;

    // Among non-active members, COMPLETED comes before LEFT
    if (!isActiveA && !isActiveB) {
      if (isCompletedA && isLeftB) return -1;
      if (isLeftA && isCompletedB) return 1;
    }

    // If both have same status (both active, both completed, or both left), then sort by tag
    // Members with tag should be sorted after members without tag
    if (hasTagA && !hasTagB) return 1;
    if (!hasTagA && hasTagB) return -1;

    // If both have same status and same tag status, sort by role order
    const roleA = ROLE_ITEMS.find((item) => item.name === a.role);
    const roleB = ROLE_ITEMS.find((item) => item.name === b.role);

    // Get the order values, default to high number for unknown roles
    const orderA = roleA?.data?.order ?? 999;
    const orderB = roleB?.data?.order ?? 999;

    // Sort by order (ascending)
    return orderA - orderB;
  });

export const formatProjectMembers = (members: ProjectMember[]) =>
  members.map((member) => ({
    ...member,
    avatar: member.avatar || FALLBACK_SRC.AVATAR,
    color: member.tag
      ? ADDITIONAL_ROLE_COLOR_MAP[member.tag]
      : getColorBaseOnRole(member.role),
  }));

/**
 * Normalize activity data to ensure consistent structure
 */
export const normalizeActivityData = (
  data?: { activities: ProjectActivity[] | EmployeeActivity[] } | null,
): ProjectActivity | null => {
  if (!data) return null;

  // If it has activities array, get the first one
  if ('activities' in data && Array.isArray(data.activities)) {
    return data.activities[0] || null;
  }

  return null;
};

/**
 * Normalizes data to always return an array of activities
 */
export const getActivitiesArray = <T>(
  data?: { activities?: T[] } | T | null,
): T[] => {
  if (!data) return [];

  if (hasProperty(data, 'activities') && Array.isArray(data.activities)) {
    return data.activities as T[];
  }

  return [data as T];
};

export const isCompletedProject = (project: ProjectInfo) => {
  return (
    project &&
    (project.status === ProjectStatus.COMPLETED ||
      project.status === ProjectStatus.PAUSED ||
      project.archived)
  );
};

export const isNotReleasableEvent = (project: ProjectInfo, actionType = '') => {
  if (!project || !actionType) {
    return;
  }

  return (
    project.status === ProjectStatus.WAITING_FOR_KICK_OFF &&
    (actionType === ActivityType.PROJECT_RESUME ||
      actionType === ActivityType.PROJECT_RELEASE ||
      actionType === ActivityType.PROJECT_PAUSE)
  );
};

export const isNotResumableCompletedEvent = (
  project: ProjectInfo,
  actionType = '',
) => {
  if (!project || !actionType) {
    return;
  }

  return (
    project.status === ProjectStatus.COMPLETED &&
    actionType !== ActivityType.PROJECT_RESUME &&
    actionType !== ActivityType.PROJECT_ARCHIVE &&
    actionType !== ActivityType.PROJECT_UNARCHIVE
  );
};

export const isNotCompletablePausedEvent = (
  project: ProjectInfo,
  actionType = '',
) => {
  if (!project || !actionType) {
    return;
  }

  return (
    project.status == ProjectStatus.PAUSED &&
    actionType !== ActivityType.PROJECT_COMPLETE &&
    actionType !== ActivityType.PROJECT_RESUME &&
    actionType !== ActivityType.PROJECT_ARCHIVE &&
    actionType !== ActivityType.PROJECT_UNARCHIVE
  );
};

export const isNotUnarchiveInactiveEvent = (
  project: ProjectInfo,
  actionType = '',
) => {
  if (!project || !actionType) {
    return;
  }

  return project.archived && actionType !== ActivityType.PROJECT_UNARCHIVE;
};

export const isEditableEvent = (project: ProjectInfo, actionType = '') => {
  if (!project || !actionType) {
    return;
  }

  return (
    (project.status !== ProjectStatus.COMPLETED &&
      project.status !== ProjectStatus.PAUSED) ||
    (project.status === ProjectStatus.COMPLETED &&
      actionType === ActivityType.PROJECT_COMPLETE) ||
    (project.status === ProjectStatus.PAUSED &&
      actionType === ActivityType.PROJECT_PAUSE) ||
    (project.archived && actionType === ActivityType.PROJECT_ARCHIVE)
  );
};

export const getSelectedActivity = (
  activities: ProjectActivity[] | EmployeeActivity[],
  actionType: ActivityType,
  date: string,
  time: string,
) => {
  return activities.find((act) => {
    if (act.type !== actionType) return false;

    if (date) {
      if (
        !formatISODateString(act.occurredAt, 'YYYY-MM-DD')?.includes(date) ||
        (date && !formatTimeString(act.occurredAt).includes(time))
      ) {
        return false;
      }
    }

    return true;
  });
};

/**
 * Generic type guard to check if a payload is an object with a specific property
 * @param payload - The payload to check
 * @param property - The property to check for
 * @returns True if the payload is an object with the specified property
 */
export const hasProperty = <T extends string>(
  payload: unknown,
  property: T,
): payload is Record<T, unknown> =>
  typeof payload === 'object' && payload !== null && property in payload;

/**
 * Type guard to check if a payload has a 'code' property (ProjectInfo)
 * @param payload - The payload to check
 * @returns True if the payload is an object with a 'code' property
 */
export const hasCodeProperty = (payload: unknown): payload is ProjectInfo =>
  hasProperty(payload, 'code');

/**
 * Type guard to check if a payload has a 'members' property (MemberAddPayloadSocket)
 * @param payload - The payload to check
 * @returns True if the payload is an object with a 'members' property
 */
export const hasMembersProperty = (
  payload: unknown,
): payload is MemberAddPayloadSocket => hasProperty(payload, 'members');

/**
 * Type guard to check if a payload has an 'activities' property (MemberProjectActivitySocket)
 * @param payload - The payload to check
 * @returns True if the payload is an object with an 'activities' property
 */
export const hasActivitiesProperty = (
  payload: unknown,
): payload is MemberProjectActivitySocket => hasProperty(payload, 'activities');

/**
 * Type guard to check if a payload has an 'id' property
 * @param payload - The payload to check
 * @returns True if the payload is an object with an 'id' property
 */
export const hasIdProperty = (payload: unknown): payload is { id: string } =>
  hasProperty(payload, 'id');

/**
 * Combined type guard to check if payload is an object and has code property
 * @param payload - The payload to check
 * @returns True if payload is object with code property
 */
export const isProjectInfoPayload = (
  payload: unknown,
): payload is ProjectInfo => hasCodeProperty(payload);

/**
 * Combined type guard to check if payload is an object and has activities property
 * @param payload - The payload to check
 * @returns True if payload is object with activities property
 */
export const isActivityPayload = (
  payload: unknown,
): payload is MemberProjectActivitySocket => hasActivitiesProperty(payload);

/**
 * Check if value is a valid EmployeeProjectAction
 */
const isValidEmployeeProjectAction = (
  value?: string,
): value is EmployeeProjectAction => {
  return !!value && value in EMPLOYEE_PROJECT_ACTION_TITLE_MAP;
};

/**
 * Normalize project member result data from args and result
 * Project code and action come from args, all other fields come from result
 */
export const normalizeProjectMemberResult = (
  args: ArgsType,
  result?: ArgsType,
): EmployeeProjectInfoCardData | null => {
  const projectCode = args?.project;
  const actionValue = args?.action;

  if (!projectCode || !isValidEmployeeProjectAction(actionValue)) return null;

  const member = (result?.data as EmployeeProjectInfoResponse)?.member;
  if (!member) return null;

  return {
    action: actionValue,
    projectCode,
    role: member.role || '',
    mode: getValidAdditionalRole(member.tag) ?? undefined,
    updatedAt: result?.updatedAt,
  };
};

/**
 * Normalize project success message
 * Replaces technical terms with user-friendly ones (e.g., "desc" -> "description")
 * @param message - The success message to normalize
 * @returns The normalized message
 */
export const normalizeProjectSuccessMessage = (message?: string) =>
  message?.replace(/desc/i, 'description') || '';

/**
 * Normalize project info with fallback priority
 * Priority: result.data → args.value (for name field) → currentData
 * @param result - Chat action result containing data and updatedAt
 * @param currentData - Current project state as fallback
 * @param args - Chat action args containing field and value
 * @returns Normalized ProjectInfo with merged values
 */
export const normalizeProjectInfo = (
  result?: ArgsType | null,
  currentData?: Partial<ProjectInfo> | null,
  args?: ArgsType | null,
): ProjectInfo => {
  const { data, updatedAt } = result || {};
  const { field, value } = args || {};
  const response = data as ProjectInfoResponse;

  return {
    id: response?.id || currentData?.id || '',
    code: response?.code || currentData?.code || '',
    logo: response?.logo || currentData?.logo || '',
    desc: response?.desc || currentData?.desc || '',
    name:
      (field === PROJECT_INFO_FIELD.NAME && value) ||
      response?.name ||
      currentData?.name ||
      '',
    updatedAt,
  };
};

/**
 * Gets the display label for a project member action with proper pluralization
 * @param action - The member action type (optional)
 * @param memberCount - Number of members affected (optional)
 * @returns Formatted label string with proper singular/plural form
 * @example
 * getMemberActionLabel('INVITE_MEMBER', 1) // '1 member invited'
 * getMemberActionLabel('INVITE_MEMBER', 3) // '3 members invited'
 * getMemberActionLabel('CHANGE_ROLE_MEMBER', 1) // 'Role changed'
 */
export const getMemberActionLabel = (
  action?: string,
  memberCount?: number,
): string => {
  if (!action || !(action in MEMBER_ACTION_LABEL_MAP)) return 'Members updated';

  const baseLabel = MEMBER_ACTION_LABEL_MAP[action as EmployeeProjectAction];

  if (action === EmployeeProjectAction.CHANGE_ROLE_MEMBER) return baseLabel;

  const actionPart = baseLabel.replace(/^Member /, '');

  return `${toPlural('Member', memberCount || 0)} ${actionPart}`;
};

/**
 * Gets the success message for a project member action with proper pluralization
 * @param action - The member action type (optional)
 * @param memberCount - Number of members affected (optional)
 * @returns Formatted success message with proper singular/plural form
 * @example
 * getMemberActionSuccessMessage('INVITE_MEMBER', 1) // 'Member has been...'
 * getMemberActionSuccessMessage('INVITE_MEMBER', 3) // 'Members have been...'
 */
export const getMemberActionSuccessMessage = (
  action?: string,
  memberCount?: number,
): string => {
  if (!action || !(action in MEMBER_ACTION_SUCCESS_MESSAGE_MAP)) return '';

  const baseMessage =
    MEMBER_ACTION_SUCCESS_MESSAGE_MAP[action as EmployeeProjectAction];
  const isRoleChange = action === EmployeeProjectAction.CHANGE_ROLE_MEMBER;
  const isSingular = memberCount === 1;

  if (isSingular) return `Member ${baseMessage}`;

  const pluralMessage = baseMessage.replace(/^has /, 'have ');
  const memberWord = toPlural('Member', memberCount || 0);

  return isRoleChange
    ? `${memberWord} roles ${pluralMessage}`
    : `${memberWord} ${pluralMessage}`;
};

/**
 * Checks if a project event is scheduled by looking for scheduled activity in activities array
 * @param eventName - The event name to check
 * @param activities - Array of activities from API response
 * @returns boolean - Whether the event is scheduled
 */
export const isScheduledProjectEvent = (
  eventName?: ProjectEventName | string,
  activities?: ProjectActivity[],
): boolean => {
  if (!eventName || !activities?.length) return false;

  const expectedScheduleType = `project_${eventName.toLowerCase()}`;

  return activities.some(
    (activity) =>
      activity.type === ActivityType.PROJECT_SCHEDULE_ACTIVITY &&
      (activity.data as ProjectBase)?.scheduleType === expectedScheduleType,
  );
};

/**
 * Creates a natural success message for scheduled events
 * @param eventName - Event name from args
 * @param dateTime - Optional scheduled date/time
 * @example "The project has been scheduled to kick-off at 10:30 AM on Jan 31, 2026."
 * @example "The project has been scheduled to go live."
 */
export const getScheduledEventMessage = (
  eventName?: ProjectEventName | string,
  dateTime?: string,
): string => {
  let label = eventName?.toLowerCase().replace(/_/g, ' ') ?? 'event';

  if (eventName === ProjectEventName.KICK_OFF) {
    label = 'kick-off';
  }

  const formattedDateTime = formatDateTimeDisplay(dateTime);

  return `The project has been scheduled to ${label}${formattedDateTime ? ` ${formattedDateTime}` : ''}.`;
};
